import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeIsUserSelected,
  changeUser,
  clearReplyMessage,
  resetChatUser,
  updateContactFromSocket,
  updateUserStatus,
} from '../../reducers/wca/sales-support/sales-support-slice';

import { useUtility } from '../../context/UtilityContext';
import { useSocket } from '../../hooks/useSocket';
import {
  getAllContacts,
  resetUnreadCount,
} from '../../reducers/wca/sales-support/sales-support-action';
import ContactChatInfo from './components/contact-chat-info';

const loadingSpinner = () => (
  <div className="h-full flex flex-column align-items-center justify-content-center">
    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
  </div>
);

const Chats = () => {
  const customerContainerRef = useRef(null);
  const firstSelectDone = useRef(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  const {
    user,
    allotedAgent,
    agentSearchItem,
    ticketStatusFilter,
    selectedAgent,
    agentChatStatus,
    contacts,
    hasMore,
  } = useSelector((state) => state.wcaEnqChats);

  const { selectedMetadataId } = useUtility(); // Get metadataId from context
  const dispatch = useDispatch();

  let companyKey = JSON.parse(localStorage.getItem('companykey'));

  const { socket } = useSocket(companyKey?.id, selectedMetadataId);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
        console.log('Socket disconnected on Chats unmount');
      }
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    const handleMessageContactUpdate = (data) => {
      console.log('✅ Socket connected Chats:', socket.id);
      console.log('📩 message_contact_update received:', data);
      dispatch(updateContactFromSocket(data));
    };

    socket.on('message_contact_update', handleMessageContactUpdate);

    // Clean up when component unmounts or socket changes
    return () => {
      socket.off('message_contact_update', handleMessageContactUpdate);
    };
  }, [socket]);

  const fetchChats = useCallback(
    async (isInitial = true, resetPage = true) => {
      if (!selectedMetadataId) return;

      if (isInitial && resetPage) {
        setPage(0);
      }
      setLoading(true);

      try {
        const filterData = [];
        if (agentChatStatus !== 'all')
          filterData.push({ key: 'agentChatStatus', value: agentChatStatus });
        if (ticketStatusFilter) filterData.push({ key: 'ticketStatus', value: ticketStatusFilter });
        if (selectedAgent) filterData.push({ key: 'agentId', value: selectedAgent });

        await dispatch(
          getAllContacts({
            metadataid: selectedMetadataId,
            pageNo: isInitial && resetPage ? 0 : page,
            limit: 30,
            startDate: null,
            endDate: null,
            filterData,
            globalFilterValue: agentSearchItem,
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to fetch chats:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      selectedMetadataId,
      allotedAgent,
      page,
      agentChatStatus,
      ticketStatusFilter,
      selectedAgent,
      agentSearchItem,
      dispatch,
    ],
  );

  useEffect(() => {
    fetchChats(true, true);
  }, [
    selectedMetadataId,
    allotedAgent,
    agentChatStatus,
    ticketStatusFilter,
    selectedAgent,
    agentSearchItem,
  ]);

  const loadMoreCustomersChat = useCallback(() => {
    if (!hasMore || loading) return;
    setPage((prevPage) => prevPage + 1);
  }, [hasMore, loading]);

  useEffect(() => {
    if (page > 0) {
      fetchChats(false, false);
    }
  }, [page, fetchChats]);

  useEffect(() => {
    if (contacts.length > 0 && !firstSelectDone.current) {
      const firstContact = contacts[0];
      dispatch(changeIsUserSelected(true));
      dispatch(changeUser(firstContact));
      dispatch(
        resetUnreadCount({
          metadataid: selectedMetadataId,
          phone: firstContact.phone,
        }),
      );
      firstSelectDone.current = true;
    }
  }, [contacts, dispatch, selectedMetadataId]);

  useEffect(() => {
    return () => {
      dispatch(resetChatUser());
    };
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(changeUser({}));
    };
  }, [agentChatStatus, dispatch]);

  const handleSelect = (chat) => {
    dispatch(changeIsUserSelected(true));
    dispatch(changeUser(chat));
    dispatch(clearReplyMessage());
    dispatch(resetUnreadCount({ metadataid: selectedMetadataId, phone: chat.phone }));
  };

  const handleScroll = () => {
    if (customerContainerRef.current && !loading && hasMore) {
      const { scrollTop, clientHeight, scrollHeight } = customerContainerRef.current;

      const tolerance = 1;

      if (scrollTop + clientHeight + tolerance >= scrollHeight) {
        loadMoreCustomersChat();
      }
    }
  };

  return (
    <div ref={customerContainerRef} onScroll={handleScroll} className="chats flex-grow-1">
      {loading && loadingSpinner()}
      {contacts?.length !== 0
        ? contacts.map((contact, i) => {
            const isUserSelected = user?.phone == contact?.phone;
            return (
              <div
                className={`userChat ${isUserSelected && 'userChatSeleceted'}
                }`}
                key={i}
                onClick={() => handleSelect(contact)}
              >
                <ContactChatInfo contact={contact} />
              </div>
            );
          })
        : !loading && (
            <div className="h-full flex flex-column align-items-center justify-content-center">
              <span> No customers found</span>
            </div>
          )}
    </div>
  );
};

export default Chats;
